import React, { useEffect, useRef, useState } from 'react';
import { mountRainCanvas } from './matrixRainController';

// 居中标题及其逐字打字速度
const SPLASH_TITLE = 'Kendama Toolbox';
const TITLE_TYPING_INTERVAL_MS = 62;
// 退格速度，比打字稍快
const TITLE_BACKSPACE_INTERVAL_MS = 42;
// 退格前停顿，模拟发现打错后的犹豫
const TITLE_BACKSPACE_HOLD_MS = 340;

// 打字脚本：打到第 12 个字后停顿退格删掉 4 个，再重新补全
const TITLE_TYPING_SCRIPT: ReadonlyArray<{ length: number; delayMs: number }> = [
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((length) => ({
    length,
    delayMs: TITLE_TYPING_INTERVAL_MS,
  })),
  ...[11, 10, 9, 8].map((length, index) => ({
    length,
    delayMs: index === 0 ? TITLE_BACKSPACE_HOLD_MS : TITLE_BACKSPACE_INTERVAL_MS,
  })),
  ...[9, 10, 11, 12, 13, 14, 15].map((length) => ({
    length,
    delayMs: TITLE_TYPING_INTERVAL_MS,
  })),
];

// 启动动画最短展示时长，避免会话校验过快时动画一闪而过
export const MATRIX_SPLASH_MIN_DURATION_MS = 2400;

// 启动动画退场过渡总时长（含数字流融入首页背景后约 2s 的渐渐消失），
// 需与 matrix-splash-exit 那段编排里最后一个动画的结束时间一致
export const MATRIX_SPLASH_EXIT_DURATION_MS = 2900;

// 进度跑满后停留的时长。
// 进度条本身的填充过渡是 300ms，这里必须大于它，
// 否则标题和进度条会在进度条还没填满时就开始放大退场。
export const MATRIX_SPLASH_PROGRESS_SETTLE_MS = 380;

interface MatrixRainLoaderProps {
  className?: string;
  // 真实初始化进度（0~1），由实际加载步骤推进
  progress?: number;
  // 加载完成后播放淡出动画
  exiting?: boolean;
}

interface MatrixRainCanvasProps {
  className?: string;
  // 透明背景：用于首页背景层，只留下字符本身
  transparent?: boolean;
}

/**
 * 数字雨画布。
 *
 * canvas 元素由 matrixRainController 创建并挂载：
 * 内核支持 OffscreenCanvas 时整块绘制交给 Worker（主线程再忙数字流也不卡），
 * 否则回退主线程渲染。外层壳用 display:contents，canvas 的绝对定位仍相对原祖先元素计算。
 */
export const MatrixRainCanvas: React.FC<MatrixRainCanvasProps> = ({ className, transparent = false }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    return mountRainCanvas(host, className ?? '', transparent);
  }, [className, transparent]);

  return <div ref={hostRef} className="contents" aria-hidden="true" />;
};

// 应用启动初始化界面：黑客帝国风格数字雨
export const MatrixRainLoader: React.FC<MatrixRainLoaderProps> = ({
  className,
  progress = 0,
  exiting = false,
}) => {
  const [typedLength, setTypedLength] = useState(0);

  // 按脚本播放逐字打字，中途会退格纠错
  useEffect(() => {
    let stepIndex = 0;
    let timer = 0;

    const playNextStep = () => {
      const step = TITLE_TYPING_SCRIPT[stepIndex];
      if (!step) {
        return;
      }

      timer = window.setTimeout(() => {
        setTypedLength(step.length);
        stepIndex += 1;
        playNextStep();
      }, step.delayMs);
    };

    playNextStep();

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[999] overflow-hidden ${exiting ? 'matrix-splash-exit pointer-events-none' : ''} ${className ?? ''}`}
      aria-busy="true"
      aria-live="polite"
    >
      {/* 黑底单独一层：退场时先化开，让首页从后面透出来 */}
      <div aria-hidden="true" className="matrix-splash-black pointer-events-none absolute inset-0 bg-black" />
      <MatrixRainCanvas className="matrix-splash-rain absolute inset-0 h-full w-full" />
      {/* 扫描线与暗角，强化老式终端质感 */}
      <div
        aria-hidden="true"
        className="matrix-splash-film pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(180deg,rgba(56,189,248,0.05)_0px,rgba(56,189,248,0.05)_1px,transparent_1px,transparent_3px)] opacity-60"
      />
      <div
        aria-hidden="true"
        className="matrix-splash-film pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0)_32%,rgba(0,0,0,0.78)_100%)]"
      />
      <div className="matrix-splash-stage relative flex h-full w-full flex-col items-center justify-center gap-8 px-6 text-center">
        <div className="relative">
          {/* 标题周围同样用暗色模糊光晕压暗背景数字雨，与进度条保持一致的凸显效果 */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[clamp(78px,21vmin,136px)] w-[min(660px,96vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.94)_0%,rgba(0,0,0,0.7)_45%,rgba(0,0,0,0)_78%)] blur-2xl"
          />
          <div className="relative font-orbitron text-[clamp(1.05rem,4.6vmin,2.5rem)] uppercase tracking-[0.4em] text-[#cdf7ff] drop-shadow-[0_0_14px_rgba(34,211,238,0.65)]">
            {/* 用不可见全文占位，避免逐字打字时标题左右跳动 */}
            <span className="invisible" aria-hidden="true">{SPLASH_TITLE}</span>
            <span className="absolute inset-y-0 left-0 flex items-center whitespace-pre">
              {SPLASH_TITLE.slice(0, typedLength)}
              {typedLength < SPLASH_TITLE.length && (
                <span className="matrix-caret ml-1 text-[#22d3ee]">█</span>
              )}
            </span>
          </div>
        </div>
        {/* 进度条外层用暗色模糊光晕压暗背景数字雨，让进度条更凸显 */}
        <div className="relative">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[110px] w-[min(440px,88vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.94)_0%,rgba(0,0,0,0.7)_45%,rgba(0,0,0,0)_78%)] blur-2xl"
          />
          <div className="relative h-[11px] w-[min(260px,60vw)] overflow-hidden rounded-full bg-[#04161c] ring-1 ring-inset ring-[#22d3ee]/30 shadow-[0_0_20px_rgba(34,211,238,0.28),inset_0_2px_5px_rgba(0,0,0,0.85)]">
            <div
              className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-[linear-gradient(90deg,rgba(34,211,238,0.15),#22d3ee)] shadow-[0_0_12px_rgba(34,211,238,0.7)] transition-transform duration-300 ease-out"
              style={{ transform: `scaleX(${progress})` }}
            />
          </div>
        </div>
      </div>

      <style>{`
        /* 打字光标闪动 */
        @keyframes matrix-caret {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }

        .matrix-caret {
          animation: matrix-caret 620ms steps(1, end) infinite;
        }

        /* 退场编排（总时长 3900ms，需与 MATRIX_SPLASH_EXIT_DURATION_MS 一致）：
           ① 文本与进度条先退场
           ② 黑底化开，交接给首页背景层里的同一场数字雨（两层同速同列，位置不变）
           ③ 数字雨落入背景层先降到 70% 透明度，随即开始渐渐消失，约 3s 后消失干净 */
        @keyframes matrix-splash-stage-out {
          100% { opacity: 0; transform: translate3d(0, -8px, 0) scale(1.15); filter: blur(6px); }
        }
        @keyframes matrix-splash-film-out {
          100% { opacity: 0; }
        }
        @keyframes matrix-splash-fade-out {
          100% { opacity: 0; }
        }

        .matrix-splash-exit {
          will-change: opacity;
        }
        /* ① 文本与进度条：进场最早，离场刻意晚 200ms——
              留一点时间让首页内容先从下面浮上来，标题再压着它退走。
              退场同时叠加动态高斯模糊：边放大边化开，字不是「变小消失」而是「散开消失」 */
        .matrix-splash-exit .matrix-splash-stage {
          transform-origin: center center;
          animation: matrix-splash-stage-out 180ms cubic-bezier(0.4, 0, 0.7, 0.6) 200ms forwards;
        }
        /* ② 黑底、扫描线与启动层这层数字雨同步淡出：
           数字雨和黑底用同一条曲线，两层叠起来的总亮度保持恒定，
           所以交接时看到的是「同一场雨」从最上层慢慢退到首页背景层。 */
        .matrix-splash-exit .matrix-splash-black,
        .matrix-splash-exit .matrix-splash-film,
        .matrix-splash-exit .matrix-splash-rain {
          animation: matrix-splash-film-out 520ms cubic-bezier(0.4, 0, 0.7, 0.5) 100ms forwards;
        }

        /* 动效减弱：只留一次整体淡出 */
        @media (prefers-reduced-motion: reduce) {
          .matrix-splash-exit,
          .matrix-splash-exit .matrix-splash-stage,
          .matrix-splash-exit .matrix-splash-black,
          .matrix-splash-exit .matrix-splash-film,
          .matrix-splash-exit .matrix-splash-rain {
            animation: matrix-splash-fade-out 300ms ease-out forwards;
          }
        }
      `}</style>
    </div>
  );
};
